import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { NoResultPageProps } from "./noResultPage.model";
import { useBlockItemComponents } from "@companion-core/shared/app/Queries";
import { ComponentData } from "@companion-core/shared/app/Interfaces/API";
import { SelectionPushLayout } from "@companion-core/shared/app/Components/Blocks/Layouts/SelectionPushLayout/SelectionPushLayout";
import { Button } from "@companion-core/shared/app/Components/Button";
import { SkeletonCard } from "@companion-core/shared/app/Components/Cards/SkeletonCard/SkeletonCard";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { NoResultTracker } from "./NoResultTracker";

import "@companion-core/web/src/Assets/Styles/scss/noResultPage.scss";

const ListLayoutMultipleSkeleton = () => {
  return (
    <div data-testid="no-result-page__skeleton--multiple" className="no-result-page__skeleton">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
};

const ListLayoutSingleSkeleton = () => {
  return (
    <div data-testid="no-result-page__skeleton--single" className="no-result-page__skeleton">
      <SkeletonCard />
    </div>
  );
};

export const NoResultPage = ({ blockItemID, resetSearch }: NoResultPageProps) => {
  const { t, i18n } = useTranslation();
  const i18nLanguage: string = i18n?.languages[0]?.toLocaleLowerCase();
  const { data: queryComponentsData, isFetching: isQueryComponentsFetching } =
    useBlockItemComponents({
      id: blockItemID,
      lang: i18nLanguage,
      pageSize: 3,
    });

  const queryComponents: ComponentData[] = useMemo(() => {
    return (
      queryComponentsData?.pages.reduce<ComponentData[]>(
        (acc, page) => [...acc, ...(page.data?.results ?? [])],
        []
      ) ?? []
    );
  }, [queryComponentsData]);

  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const ListLayoutSkeleton = () => {
    if (!isMobileSize) {
      return <ListLayoutMultipleSkeleton />;
    } else {
      return <ListLayoutSingleSkeleton />;
    }
  };
  const contentCt = queryComponents[0]?.content_ct ?? "";

  return (
    <div className="no-result-page">
      <div className="no-result-page__texts">
        <div
          className="title-7 no-result-page__texts-title"
          data-testid="no-result-page__texts-title">
          {t("common.search.no-result.title")}
        </div>
        <div
          className="no-result-page__texts-list title-8"
          data-testid="no-result-page__texts-list">
          <span>{t("common.search.no-result.subtext")}</span>
          <ul>
            <li>{t("common.search.no-result.criteria-1")}</li>
            <li>{t("common.search.no-result.criteria-2")}</li>
            <li>{t("common.search.no-result.criteria-3")}</li>
          </ul>
        </div>
      </div>

      <div className="no-result-page__select">
        {queryComponents.length > 0 && <NoResultTracker key={contentCt} contentCt={contentCt} />}
        <div
          className="title-5 no-result-page__select-title"
          data-testid="no-result-page__select-title">
          <span>{t("common.search.no-result.select.title")}</span>
        </div>
        {isQueryComponentsFetching ? (
          <div className="no-result-page__skeleton-wrapper">
            <ListLayoutSkeleton />
          </div>
        ) : (
          <SelectionPushLayout cardList={queryComponents} />
        )}
        <Button
          label={t("common.search.no-result.select.CTA")}
          testID="no-result-page__select-CTA"
          size="large"
          onClick={() => resetSearch?.()}
        />
      </div>
    </div>
  );
};
